import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react';
import type {ItemParams} from 'react-contexify';
import {contextMenu} from 'react-contexify';
import isEqual from 'react-fast-compare';

import NiceModal from '@ebay/nice-modal-react';
import {Observer, useLocalObservable} from 'mobx-react';

import type {ScheduleItem} from '@yourcoach/shared/modules/program/cru/schedule/types';
import type {ProgramType} from '@yourcoach/shared/utils/entity/program';
import type {GetComponentProps} from '@yourcoach/shared/utils/utility-types';

import {i18n} from '@yourcoach/i18n';
import CalendarIcon from '@yourcoach/shared/assets/icons/primary/Calendar.svg';
import CheckboxIcon from '@yourcoach/shared/assets/icons/primary/Tick Square.svg';
import CameraIcon from '@yourcoach/shared/assets/icons/primary/Video.svg';
import {
  CRUProgramScheduleStore,
  ProgramScheduleListItem,
} from '@yourcoach/shared/modules/program/cru/schedule';
import {
  ProgramScheduleItemTypeEnum,
  ProgramTypeEnum,
} from '@yourcoach/shared/utils/entity/program';
import {emitter} from '@yourcoach/shared/utils/eventEmitter';
import {logger} from '@yourcoach/shared/utils/logger';
import {useMobxStore} from '@yourcoach/shared/utils/store';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';

import Button from '@src/components/Button';
import NoResultsHeader from '@src/components/NoResultsHeader';
import {ContextMenu} from '@src/v2/components/ContextMenu';

import type {TabProps, TabRef} from '../useTabs';
import type {ResolveType as CRUProgramScheduleConferenceModalResolvedType} from './ScheduleConference/types';
import type {ResolveType as CRUProgramScheduleTaskModalResolvedType} from './ScheduleTask/types';

import {PROGRAM_TYPE_CHANGE_EVENT} from '../Details';
import {CRUProgramScheduleConferenceModal} from './ScheduleConference';
import {CRUProgramScheduleTaskModal} from './ScheduleTask';

import styles from './index.module.css';

const SCHEDULE_EVENT_CONTEXT_MENU_ID = 'schedule_event_context_menu';

const CRUProgramSchedule = forwardRef<TabRef, TabProps>(
  ({isIndividual, program}, ref) => {
    const store = useMobxStore(() => new CRUProgramScheduleStore(program));

    useImperativeHandle(ref, () => ({
      getData,
    }));

    type CTX = {
      item: null | ScheduleItem;
      setItem: (item: null | ScheduleItem) => void;
    };

    const contextMenuController = useLocalObservable<CTX>(() => ({
      item: null,
      setItem(item: null | ScheduleItem) {
        this.item = item;
      },
    }));

    useEffect(() => {
      if (program) {
        store.setProgram(program);
      }
    }, [store, program]);

    const onProgramTypeChange = useCallback(
      (type: ProgramType) => {
        store.setIsIndividual(type.id === ProgramTypeEnum.INDIVIDUAL.id);
      },
      [store],
    );

    useEffect(() => {
      emitter.on(PROGRAM_TYPE_CHANGE_EVENT, onProgramTypeChange);

      return () => {
        emitter.off(PROGRAM_TYPE_CHANGE_EVENT, onProgramTypeChange);
      };
    }, [onProgramTypeChange]);

    const getData = useCallback(() => {
      return store.getData();
    }, [store]);

    const onAddConferenceButtonClick = useCallback(async () => {
      logger.event('add_live_session_tap');

      const conference: CRUProgramScheduleConferenceModalResolvedType =
        await NiceModal.show(CRUProgramScheduleConferenceModal, {
          program: program!,
        });

      if (conference) {
        store.addConference(conference);
      }
    }, [program, store]);

    const onAddTaskButtonClick = useCallback(async () => {
      logger.event('add_task_tap');

      const task: CRUProgramScheduleTaskModalResolvedType =
        await NiceModal.show(CRUProgramScheduleTaskModal, {
          program: program!,
        });

      if (task) {
        store.addTask(task);
      }
    }, [program, store]);

    const onClearButtonClick = useCallback(async () => {
      store.clear();
    }, [store]);

    const onScheduleItemClick = useCallback(
      async (item: ScheduleItem) => {
        contextMenu.hideAll();

        if (item.type.id === ProgramScheduleItemTypeEnum.CONFERENCE.id) {
          const conference: CRUProgramScheduleConferenceModalResolvedType =
            // @ts-expect-error: Property 'speaker_ids' is missing
            await NiceModal.show(CRUProgramScheduleConferenceModal, {
              conference: {
                ...item,
                day: store.getScheduledItemStartDay(item),
              },
              program: program!,
            });

          if (conference) {
            store.updateScheduleEvent(
              conference,
              ProgramScheduleItemTypeEnum.CONFERENCE,
            );
          }
        } else if (item.type.id === ProgramScheduleItemTypeEnum.TASK.id) {
          const task: CRUProgramScheduleTaskModalResolvedType =
            // @ts-expect-error: TODO: fix this later
            await NiceModal.show(CRUProgramScheduleTaskModal, {
              program: program!,
              task: {...item, day: store.getScheduledItemStartDay(item)},
            });

          if (task) {
            store.updateScheduleEvent(task, ProgramScheduleItemTypeEnum.TASK);
          }
        }
      },
      [program, store],
    );

    const onUpdateButtonClick = useCallback(
      ({props}: ItemParams<{item: ScheduleItem}>) => {
        if (props) {
          const {item} = props;

          contextMenu.hideAll();

          onScheduleItemClick(item);
        }
      },
      [onScheduleItemClick],
    );

    const onDeleteButtonClick = useCallback(
      ({props}: ItemParams<{item: ScheduleItem}>) => {
        if (props) {
          const {item} = props;

          store.removeScheduleItem(item);
        }
      },
      [store],
    );

    const onScheduleEventMoreIconClick: GetComponentProps<
      typeof ProgramScheduleListItem
    >['onMoreIconClick'] = useCallback(
      (e, item) => {
        contextMenuController.setItem(item);

        contextMenu.show({
          event: e.event.DOMEvent!,
          id: SCHEDULE_EVENT_CONTEXT_MENU_ID,
          props: {
            item,
          },
        });
      },
      [contextMenuController],
    );

    return (
      <>
        <div className={styles.addButtonsContainer}>
          <Button
            data-test-id={TestID.create_program_schedule.button.add_task}
            onClick={onAddTaskButtonClick}
          >
            <CheckboxIcon />
            {i18n.t('shared:CRUProgramSchedule.add_task_button')}
          </Button>
          {!isIndividual ? (
            <Button
              data-test-id={
                TestID.create_program_schedule.button.add_conference
              }
              onClick={onAddConferenceButtonClick}
            >
              <CameraIcon />
              {i18n.t('shared:CRUProgramSchedule.add_conference_button')}
            </Button>
          ) : null}
          <Observer>
            {() =>
              !store.isEmpty ? (
                <Button
                  className={styles.clearButton}
                  data-test-id={TestID.create_program_schedule.button.clear}
                  onClick={onClearButtonClick}
                >
                  {i18n.t('shared:CRUProgramSchedule.clear_button')}
                </Button>
              ) : null
            }
          </Observer>
        </div>
        <Observer>
          {() =>
            store.isEmpty ? (
              <div className={styles.noResultsContainer}>
                <NoResultsHeader
                  icon={CalendarIcon}
                  text={
                    isIndividual
                      ? i18n.t(
                          'shared:CRUProgramSchedule.no_results_label_individual',
                        )
                      : i18n.t('shared:CRUProgramSchedule.no_results_label')
                  }
                />
              </div>
            ) : (
              <div className={styles.list}>
                {store.sections.map(section => (
                  <div key={section.title}>
                    <div className={styles.sectionHeaderContainer}>
                      <h4 className={styles.sectionHeader}>{section.title}</h4>
                      <span className={styles.line} />
                    </div>
                    {section.data.map(item => (
                      <div className={styles.listItem} key={item.uuid}>
                        <ProgramScheduleListItem
                          item={item}
                          onClick={onScheduleItemClick}
                          onMoreIconClick={onScheduleEventMoreIconClick}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )
          }
        </Observer>
        <Observer>
          {() => (
            <ContextMenu.Menu
              animation="fade"
              id={SCHEDULE_EVENT_CONTEXT_MENU_ID}
              preventDefaultOnKeydown={false}
            >
              <ContextMenu.Item onClick={onUpdateButtonClick}>
                {
                  /**
                   * @i18n_parser
                   *
                   * `shared:CRUProgramSchedule.more_update_button.conference.regular`
                   * `shared:CRUProgramSchedule.more_update_button.conference.recurring`
                   * `shared:CRUProgramSchedule.more_update_button.task.regular`
                   * `shared:CRUProgramSchedule.more_update_button.task.recurring`
                   */
                  contextMenuController.item
                    ? i18n.t(
                        `shared:CRUProgramSchedule.more_update_button.${contextMenuController.item.type.id}.${contextMenuController.item.repeat.type === 'once' ? 'regular' : 'recurring'}`,
                      )
                    : ''
                }
              </ContextMenu.Item>
              <ContextMenu.Item onClick={onDeleteButtonClick} variant="error">
                {
                  /**
                   * @i18n_parser
                   *
                   * `shared:CRUProgramSchedule.more_delete_button.conference.regular`
                   * `shared:CRUProgramSchedule.more_delete_button.conference.recurring`
                   * `shared:CRUProgramSchedule.more_delete_button.task.regular`
                   * `shared:CRUProgramSchedule.more_delete_button.task.recurring`
                   */
                  contextMenuController.item
                    ? i18n.t(
                        `shared:CRUProgramSchedule.more_delete_button.${contextMenuController.item.type.id}.${contextMenuController.item.repeat.type === 'once' ? 'regular' : 'recurring'}`,
                      )
                    : ''
                }
              </ContextMenu.Item>
            </ContextMenu.Menu>
          )}
        </Observer>
      </>
    );
  },
);

export default memo(CRUProgramSchedule, isEqual);
